import { Grid } from '@mui/material';
import { VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { HeaderContent } from '../../../../../components/HeaderContent';
import * as routing from '../../../../../constants/Routing';
import { UsersTableColumnKeys } from '../../../../../context/backoffice/tableColumns/users';
import { sortDataByTableColumns, TableColumnsSortConfig } from '../../../../../context/shared/tableColumns';
import { User } from '../../../../../domain/backoffice/user/User';
import { useSearchUsers } from '../../../../../hooks/backoffice/users/useSearchUsers';
import { useSetUsersColumns } from '../../../../../hooks/backoffice/users/useSetUsersColumns';
import { useAsUser } from '../../../../../hooks/session/useAsUser';
import { useRetrieveAdminUser } from '../../../../../hooks/user/useRetrieveAdminUser';
import { useRetrieveUser } from '../../../../../hooks/user/useRetrieveUser';
import { DataTable } from '../../../../../react-components/data-table/DataTable';
import Text from '../../../../../react-components/Text';
import { getUsersColumns } from './BackofficeClientsUsersListColumns';
import { BackofficeClientsUsersListFilters } from './BackofficeClientsUsersListFilters';

export const BackofficeClientsUsersList = () => {
  const navigate = useNavigate();
  const { data: user } = useRetrieveUser();
  const { data: adminUser } = useRetrieveAdminUser();
  const { beAsUser } = useAsUser();
  const { tableColumns } = useSetUsersColumns();

  const [searchParameters, setSearchParameters] = useState<{ limit: number; offset: number }>({
    offset: 0,
    limit: 20,
  });

  const [sortUsersInfo, setSortUsersInfo] = useState<TableColumnsSortConfig<UsersTableColumnKeys>>({
    orderBy: `created_at`,
    order: `desc`,
  });

  const {
    data: usersPaginated,
    run: searchUsers,
    isLoading,
  } = useSearchUsers({
    offset: searchParameters.offset,
    limit: searchParameters.limit,
    sortBy: sortUsersInfo.orderBy,
    sortDirection: sortUsersInfo.order,
  });

  useEffect(() => {
    searchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParameters, sortUsersInfo]);

  const { docs, limit, page, pages, total } = usersPaginated || {};

  const handleAsUser = (id: string) => {
    beAsUser({ id });
    window.open(routing.PLANNER.ROOT, `_blank`);
  };

  const handleCopyEmail = (email: string) => {
    const el = document.createElement(`textarea`);
    el.value = email;
    const helper = document.getElementById(`copy-email-helper`);
    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    helper.appendChild(el);
    el.select();
    document.execCommand(`copy`);
    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    helper.removeChild(el);
  };

  const goToDetails = ({ target, id }: { target: `user` | `organization` | `project`; id: string }) => {
    switch (target) {
      case `user`:
        navigate(`${routing.BACKOFFICE.CLIENTS.USERS}/details/${id}`);
        break;
      case `organization`:
        navigate(`${routing.BACKOFFICE.CLIENTS.ORGANIZATIONS}/details/${id}`);
        break;
      case `project`:
        navigate(`${routing.BACKOFFICE.CLIENTS.PROJECTS}/details/${id}`);
        break;
      default:
        break;
    }
  };

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ backgroundColor: `white`, padding: `16px 22px`, zIndex: 2, height: `100%`, overflowY: `auto` }}
    >
      <Grid container direction="column" style={{ padding: `16px 22px`, height: `100%`, width: `100%` }} wrap="nowrap">
        <Grid
          style={{ paddingTop: `8px`, paddingBottom: `16px` }}
          container
          item
          justifyContent="space-between"
          direction="row"
          wrap="nowrap"
        >
          <HeaderContent
            titleIntlId={`backoffice.clients.users_num`}
            titleIntlValues={{ num: !!total && total > 0 ? total : `` }}
            styles={{
              container: {
                backgroundColor: `transparent`,
                width: `unset`,
              },
              title: {
                width: `200px`,
              },
            }}
          />
          <BackofficeClientsUsersListFilters />
        </Grid>

        {isLoading || (total !== undefined && total > 0) ? (
          <Grid item container style={{ overflowX: `auto`, overflowY: `hidden`, height: `100%`, width: `100%` }}>
            {isLoading || (docs ?? []).length > 0 ? (
              <Grid container style={{ width: `100%`, height: `100%`, position: `relative` }}>
                <div style={{ height: `100%`, width: `100%`, overflowX: `auto`, position: `absolute` }}>
                  <DataTable
                    loading={isLoading}
                    showCheckboxes={false}
                    docs={sortDataByTableColumns({
                      data: docs ?? [],
                      tableColumns,
                      sortInfo: sortUsersInfo,
                    }).map((user: User) => ({ doc: user }))}
                    limit={limit}
                    page={page}
                    pages={pages}
                    total={total ?? 0}
                    noFooter={isLoading}
                    onChangePage={({ offset, limit }) =>
                      setSearchParameters({
                        ...searchParameters,
                        offset,
                        limit,
                      })
                    }
                    hover
                    sortBy={sortUsersInfo.orderBy}
                    sortDirection={sortUsersInfo.order}
                    onColumnSort={(column: any, order: `asc` | `desc`) => {
                      setSortUsersInfo({ order, orderBy: column });
                    }}
                    columns={[
                      ...getUsersColumns({
                        loggedUser: user,
                        adminUser,
                        tableColumns,
                        handleAsUser,
                        handleCopyEmail,
                        onRefreshContent: () => searchUsers(),
                        goToDetails,
                      }),
                    ]}
                  />
                </div>
              </Grid>
            ) : (
              <Grid
                container
                direction="column"
                alignItems="center"
                style={{
                  height: `100%`,
                  padding: `32px`,
                }}
              >
                <Text variant="body-large" intlId="no_results" />
              </Grid>
            )}
          </Grid>
        ) : (
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            style={{ height: `100%`, width: `100%`, marginTop: `50px` }}
          >
            <VSpacer medium />
            <Text variant="body-medium" intlId="backoffice.clients.no_results_with_this_filters" />
          </Grid>
        )}
      </Grid>

      <div id="copy-email-helper" />
    </Grid>
  );
};
